/**
 * The rules a signature must satisfy to survive every client the team sends from — Apple Mail
 * (macOS, iOS), Thunderbird and Outlook (classic Word engine, new Outlook) — and to arrive looking
 * the same at the other end.
 *
 * Run twice: by `signature-lint.spec.ts` over every form the page can produce, and by the page
 * itself on the live preview, which refuses to copy or download a signature that fails. Each rule
 * names the client behaviour it stands in for, because a rule nobody can justify gets deleted the
 * first time it is inconvenient.
 *
 * Deliberately a scanner over the markup string, not a DOM parse: the input is our own renderer's
 * output, and what a client receives IS the string — a DOM would normalise away exactly the
 * details (attribute quoting, entities, stray characters) some of these rules are about.
 */

import { MAX_SIGNATURE_HTML_LENGTH } from './signature';

/** Every rule the lint can report. */
export type SignatureLintRule =
  | 'ELEMENT_NOT_ALLOWED'
  | 'CLASS_OR_ID'
  | 'EVENT_HANDLER'
  | 'CSS_PROPERTY_NOT_ALLOWED'
  | 'CSS_URL'
  | 'IMG_NOT_HTTPS'
  | 'IMG_SIZE'
  | 'IMG_ALT'
  | 'LINK_SCHEME'
  | 'FONT_MISSING'
  | 'FONT_NOT_SAFE'
  | 'COLOR_MISSING'
  | 'NON_ASCII'
  | 'TOO_LONG';

/** One violation. `excerpt` is the offending tag or character, for a readable failure. */
export interface SignatureLintViolation {
  readonly rule: SignatureLintRule;
  readonly excerpt: string;
}

/**
 * The only elements a signature may contain.
 *
 * - `style`, `link`, `head`: dropped by every signature editor, so any rule inside is lost.
 * - `svg`: not rendered by Outlook's Word engine at all.
 * - `div`, `p`: Word gives `p` its own margins, and a `div`'s layout is where the clients disagree
 *   most — layout is tables, text breaks are `br`.
 * - `script`, `iframe`, `form`, `object`: stripped or blocked everywhere, and not ours to send.
 */
const ALLOWED_ELEMENTS = new Set([
  'table',
  'tbody',
  'tr',
  'td',
  'a',
  'img',
  'span',
  'br',
]);

/**
 * CSS the Word engine ignores or the clients disagree on. A property on this list does not break
 * the signature in Apple Mail — it makes Outlook look different from Apple Mail, which is the
 * failure this whole feature exists to avoid.
 */
const FORBIDDEN_CSS = [
  /^margin/,
  /^border-radius$/,
  /^box-shadow$/,
  /^position$/,
  /^float$/,
  /^flex/,
  /^grid/,
  /^gap$/,
  /^transform/,
  /^background-image$/,
  /^opacity$/,
];

/** Families present on macOS, iOS and Windows alike. The FIRST family of a stack must be one. */
const SAFE_FIRST_FONTS = new Set([
  'arial',
  'helvetica',
  'verdana',
  'tahoma',
  'georgia',
  'times new roman',
  'trebuchet ms',
  'courier new',
]);

/** Elements that carry text and therefore must state their own font and colour. */
const TEXT_ELEMENTS = new Set(['td', 'a', 'span']);

const TAG = /<\s*(\/?)\s*([a-zA-Z][a-zA-Z0-9-]*)([^>]*)>/g;
const ATTRIBUTE =
  /([a-zA-Z_:][-a-zA-Z0-9_:.]*)(?:\s*=\s*(?:"([^"]*)"|'([^']*)'|([^\s"'>/]+)))?/g;

/** A tag's attributes, lower-cased names, values as written. */
function attributesOf(source: string): Map<string, string> {
  const out = new Map<string, string>();
  for (const match of source.matchAll(ATTRIBUTE)) {
    out.set(match[1].toLowerCase(), match[2] ?? match[3] ?? match[4] ?? '');
  }
  return out;
}

/** A `style` attribute as property → value, lower-cased property names. */
export function parseInlineStyle(style: string): Map<string, string> {
  const out = new Map<string, string>();
  for (const declaration of style.split(';')) {
    const colon = declaration.indexOf(':');
    if (colon < 0) continue;
    const property = declaration.slice(0, colon).trim().toLowerCase();
    const value = declaration.slice(colon + 1).trim();
    if (property) out.set(property, value);
  }
  return out;
}

/** Undoes the few entities our renderer writes into attribute values. */
function decodeAttribute(value: string): string {
  return value
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&amp;/g, '&');
}

function lintImage(
  tag: string,
  attributes: Map<string, string>,
  style: Map<string, string>,
  violations: SignatureLintViolation[],
): void {
  // Remote HTTPS only. `cid:` needs an attachment a signature cannot carry, `data:` is stripped
  // by Gmail and blocked by Outlook, and `http:` is refused by clients that load mixed content.
  const src = decodeAttribute(attributes.get('src') ?? '');
  if (!/^https:\/\/[^\s"'<>]+$/.test(src)) {
    violations.push({ rule: 'IMG_NOT_HTTPS', excerpt: tag });
  }
  // Word lays an image out at its NATURAL size unless the attributes say otherwise, and some
  // Outlook builds rescale by the display's DPI unless the CSS says the same thing again.
  const width = attributes.get('width') ?? '';
  const height = attributes.get('height') ?? '';
  const sized =
    /^\d+$/.test(width) &&
    /^\d+$/.test(height) &&
    style.get('width') === `${width}px` &&
    style.get('height') === `${height}px`;
  if (!sized) violations.push({ rule: 'IMG_SIZE', excerpt: tag });
  // What the reader sees while Outlook is still blocking images.
  if (!(attributes.get('alt') ?? '').trim()) {
    violations.push({ rule: 'IMG_ALT', excerpt: tag });
  }
}

function lintTextElement(
  tag: string,
  style: Map<string, string>,
  violations: SignatureLintViolation[],
): void {
  // Nothing may be inherited: an inherited font is the composing client's default, which is how a
  // signature ends up in Calibri in Outlook and Helvetica in Apple Mail.
  const family = style.get('font-family');
  if (!family) {
    violations.push({ rule: 'FONT_MISSING', excerpt: tag });
  } else {
    const first = family
      .split(',')[0]
      .trim()
      .replace(/^['"]|['"]$/g, '');
    // Word falls back to Times New Roman — not to the next family — when the first is missing.
    if (!SAFE_FIRST_FONTS.has(first.toLowerCase())) {
      violations.push({ rule: 'FONT_NOT_SAFE', excerpt: tag });
    }
  }
  if (!style.get('color')) {
    violations.push({ rule: 'COLOR_MISSING', excerpt: tag });
  }
}

/**
 * Lints a signature fragment. An empty result is a signature every target client renders alike.
 *
 * @param html - The fragment as produced by `renderSignatureHtml`.
 */
export function lintSignatureHtml(html: string): SignatureLintViolation[] {
  const violations: SignatureLintViolation[] = [];

  if (html.length > MAX_SIGNATURE_HTML_LENGTH) {
    violations.push({ rule: 'TOO_LONG', excerpt: `${html.length} characters` });
  }

  // Apple Mail's `.mailsignature` declares `charset=us-ascii`. Printable ASCII only.
  const nonAscii = /[^\x20-\x7e]/.exec(html);
  if (nonAscii) {
    violations.push({
      rule: 'NON_ASCII',
      excerpt: `U+${(nonAscii[0].codePointAt(0) ?? 0).toString(16).toUpperCase().padStart(4, '0')}`,
    });
  }

  for (const match of html.matchAll(TAG)) {
    const [tag, closing, rawName, rest] = match;
    const name = rawName.toLowerCase();
    if (!ALLOWED_ELEMENTS.has(name)) {
      violations.push({ rule: 'ELEMENT_NOT_ALLOWED', excerpt: tag });
      continue;
    }
    if (closing) continue;

    const attributes = attributesOf(rest);
    // `class` and `id` point at a stylesheet the signature does not have.
    if (attributes.has('class') || attributes.has('id')) {
      violations.push({ rule: 'CLASS_OR_ID', excerpt: tag });
    }
    if (
      [...attributes.keys()].some((attribute) => attribute.startsWith('on'))
    ) {
      violations.push({ rule: 'EVENT_HANDLER', excerpt: tag });
    }

    const style = parseInlineStyle(
      decodeAttribute(attributes.get('style') ?? ''),
    );
    for (const [property, value] of style) {
      if (FORBIDDEN_CSS.some((pattern) => pattern.test(property))) {
        violations.push({
          rule: 'CSS_PROPERTY_NOT_ALLOWED',
          excerpt: `${property} in ${tag}`,
        });
      }
      if (/url\s*\(/i.test(value)) {
        violations.push({ rule: 'CSS_URL', excerpt: tag });
      }
    }

    if (name === 'img') lintImage(tag, attributes, style, violations);
    if (name === 'a') {
      const href = decodeAttribute(attributes.get('href') ?? '');
      const allowed =
        /^https:\/\/[^\s"'<>]+$/.test(href) ||
        /^mailto:[^\s"'<>]+@[^\s"'<>]+$/.test(href) ||
        /^tel:\+[1-9]\d{6,14}$/.test(href);
      if (!allowed) violations.push({ rule: 'LINK_SCHEME', excerpt: tag });
    }
    if (TEXT_ELEMENTS.has(name)) lintTextElement(tag, style, violations);
  }

  return violations;
}
